import axios, { AxiosInstance, InternalAxiosRequestConfig } from "axios";
import Cookies from 'js-cookie';
import Swal from 'sweetalert2';

const BASE_URL = process.env.REACT_APP_BASE_URL;

export const axiosInstance: AxiosInstance = axios.create({
  baseURL: BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  }
});

export const axiosInstanceNoToken: AxiosInstance = axios.create({
  baseURL: BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  }
});

axiosInstanceNoToken.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const language = localStorage.getItem('selectedLanguage') || 'az';
    config.headers['Accept-Language'] = language;
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

axiosInstanceNoToken.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  if (config.method && ['put', 'patch', 'delete'].includes(config.method.toLowerCase())) {
    config.url = config.url?.replace(/\/$/, '');
  }
  return config;
});

axiosInstanceNoToken.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 429) {
      if (!Swal.isVisible()) {
        Swal.fire({
          icon: 'error',
          title: 'Xəta!',
          text: 'Çox sayda sorğu göndərdiniz. Zəhmət olmasa bir az sonra yenidən cəhd edin.',
          confirmButtonText: 'Tamam',
          confirmButtonColor: '#a80067',
        });
      }
      return Promise.reject(error);
    }
    return Promise.reject(error);
  }
);

function decodeJWT(token: string): any {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('JWT decode error:', error);
    return null;
  }
}

function getUserInfo(token: string | undefined): any {
  if (!token) return null;
  return decodeJWT(token);
}

// Activity Tracker
let inactivityTimer: NodeJS.Timeout | null = null;
const INACTIVITY_TIME = 10 * 60 * 1000; // 10 dəqiqə (millisaniyələrlə)

function resetInactivityTimer(): void {
  if (inactivityTimer) {
    clearTimeout(inactivityTimer);
  }

  inactivityTimer = setTimeout(() => {
    removeTokenFromAxiosInstance();

  }, INACTIVITY_TIME);
}

export function initializeActivityTracking(): void {
  const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];

  events.forEach(event => {
    document.addEventListener(event, resetInactivityTimer, true);
  });

  resetInactivityTimer();
}

export function cleanupActivityTracking(): void {
  const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];

  events.forEach(event => {
    document.removeEventListener(event, resetInactivityTimer, true);
  });

  if (inactivityTimer) {
    clearTimeout(inactivityTimer);
  }
}


axiosInstance.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const language = localStorage.getItem('selectedLanguage') || 'az';
    config.headers['Accept-Language'] = language;

    const accessToken = Cookies.get('accessToken');
    if (accessToken) {
      config.headers['Authorization'] = `Bearer ${accessToken}`;
    }

    resetInactivityTimer();

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

axiosInstance.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  if (config.method && ['put', 'patch', 'delete'].includes(config.method.toLowerCase())) {
    config.url = config.url?.replace(/\/$/, '');
  }
  return config;
});

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 429) {
      if (!Swal.isVisible()) {
        Swal.fire({
          icon: 'error',
          title: 'Xəta!',
          text: 'Çox sayda sorğu göndərdiniz. Zəhmət olmasa bir az sonra yenidən cəhd edin.',
          confirmButtonText: 'Tamam',
          confirmButtonColor: '#a80067',
        });
      }
      return Promise.reject(error);
    }

    if (error.response?.status === 401 && !originalRequest._retry) {
      // Don't try to refresh for auth endpoints
      const authEndpoints = ['/auth/login', '/auth/verify_otp', '/auth/send_otp', '/auth/token'];
      const isAuthRequest = authEndpoints.some(endpoint => originalRequest.url?.includes(endpoint));

      if (isAuthRequest) {
        return Promise.reject(error);
      }

      originalRequest._retry = true;

      try {
        const refreshToken = Cookies.get('refreshToken');

        if (!refreshToken) {
          throw new Error('Refresh token tapılmadı');
        }

        const response = await axios.post(`${BASE_URL}/auth/token/refresh`, {
          refresh: refreshToken
        });

        if (response.data.access) {
          setAccessToken(response.data.access);
          originalRequest.headers['Authorization'] = `Bearer ${response.data.access}`;
          return axiosInstance(originalRequest);
        }
      } catch (refreshError) {
        console.error('Token refresh failed:', refreshError);
        removeTokenFromAxiosInstance();
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

export function setAccessToken(token: string): void {
  axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  Cookies.set('accessToken', token, {
    secure: process.env.NODE_ENV === 'production',
    sameSite: 'strict'
  });

  const userInfo = getUserInfo(token);
  if (userInfo) {
    localStorage.setItem('user_info', JSON.stringify(userInfo));
  }
}

export const removeTokenFromAxiosInstance = async (): Promise<void> => {
  try {
    delete axiosInstance.defaults.headers.common['Authorization'];
    localStorage.clear();

    Cookies.remove('accessToken');
    Cookies.remove('refreshToken');

    cleanupActivityTracking();

    window.location.href = '/login';
  } catch (error) {
    console.error('Logout error:', error);
    delete axiosInstance.defaults.headers.common['Authorization'];
    localStorage.clear();
    Cookies.remove('accessToken');
    Cookies.remove('refreshToken');
    cleanupActivityTracking();
    window.location.href = '/login';
  }
};

export {
  decodeJWT,
  getUserInfo
};